import { Box } from "@mui/material";
import SHQuickAction from "../../theme/Modal/SHQuickAction";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { DropdownBase, DropdownBaseV2 } from "../../theme/dropdowns/Dropdown";
import { condition_object_type } from "../../../utils/staticEnums";

export const CRValueMultiSelect = ({
  ruleline,
  index,
  selectRuleLineValue,
  errorMap,
  colors,
  t,
  conditionValueSelect,
  getObjects,
  getListOptions,
  lazyMount,
}) => {
  const rulelineObjects = getObjects(ruleline, t);
  const isFieldType = ruleline.objectType === condition_object_type.ORDERFIELD || ruleline.objectType === condition_object_type.PRODUCTFIELD;
  const listOptions = getListOptions(ruleline?.valueType, rulelineObjects, t, false, ruleline?.listValues);

  // For orderfield/productfield, convert stored values to indices for selection
  const getSelectedIndices = () => {
    if (isFieldType && ruleline.listValues && Array.isArray(ruleline.listValues) && ruleline.value) {
      return ruleline.value
        .map(val => ruleline.listValues.indexOf(val))
        .filter(idx => idx !== -1);
    }
    return ruleline.value || [];
  };

  // For orderfield/productfield, compare by name (value); for others, compare by id
  const valueArray = Array.isArray(ruleline.value) ? ruleline.value : [];
  const filteredOptions = listOptions?.filter(option =>
    isFieldType
      ? valueArray.includes(option.name)
      : valueArray.includes(option.id)
  ) || [];

  const longTextThreshold = 20;
  const longTextExists = filteredOptions.some(option => option.name?.split(" ").length > 1);
  const sortedOptions = longTextExists
    ? [
        ...filteredOptions.filter(opt => opt.name?.length <= longTextThreshold),
        ...filteredOptions.filter(opt => opt.name?.length > longTextThreshold),
      ]
    : filteredOptions;

  const firstSelectedOption = isFieldType
    ? listOptions?.find(opt => opt.name === valueArray[0])
    : listOptions?.find(opt => opt.id === valueArray[0]);

  // Handle value transformation internally for orderfield/productfield
  const handleChange = (selected) => {
    if (isFieldType && ruleline.listValues && Array.isArray(ruleline.listValues)) {
      // For orderfield/productfield: selected contains indices, convert to values
      const transformedValues = selected
        .map(idx => ruleline.listValues[idx])
        .filter(val => val !== undefined);
      selectRuleLineValue(transformedValues, index);
    } else {
      // For other types: pass through directly
      selectRuleLineValue(selected, index);
    }
  };

  return (
    <SHQuickAction
      anchorOrigin={{ vertical: "center", horizontal: "right" }}
      transformOrigin={{ vertical: "center", horizontal: "left" }}
      component={
        <Box sx={{ ml: 0.5 }}>
          <Subheading sx={{ mt: 0.5 }}>
            {t(conditionValueSelect?.find(value => value.key === ruleline.key)?.name)}
          </Subheading>
          <Box
            sx={{
              mt: 1,
              maxHeight: 500,
              width: 300,
              display: "grid",
              gridTemplateColumns: longTextExists ? "1fr 1fr" : "1fr 1fr 1fr",
              gap: longTextExists ? "12px 16px" : 1,
            }}
          >
            {sortedOptions?.map((option, idx) => (
              <Box key={idx}>
                <Text>{option.name}</Text>
              </Box>
            ))}
          </Box>
        </Box>
      }
      sx={{
        modal: {
          body: { ml: 10 },
          triangle: { transform: "rotate(-90deg)", top: "calc(50% - 8px)", left: "58px" },
        },
      }}
    >
      {({ anchorEl, onMouseEnter, onMouseLeave, onTouchStart }) => (
        <Box
          className="shibuya"
          aria-owns={anchorEl ? "simple-popover" : undefined}
          aria-haspopup="true"
          onMouseEnter={valueArray.length > 1 ? onMouseEnter : null}
          onMouseLeave={valueArray.length > 1 ? onMouseLeave : null}
          onTouchStart={valueArray.length > 1 ? onTouchStart : null}
          sx={{
            ml: "auto",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: colors.grey[100],
            height: 32,
            minWidth: 120,
            borderRadius: 4,
            "&:hover": {
              background: colors.grey[200],
              cursor: "pointer",
            },
            "&.error": {
              background: colors.red[100],
            },
          }}
        >
          <DropdownBaseV2
            // key={`dbase-${ruleline.value}`}
            lazyMount={lazyMount}
            displayName="name"
            valueKey="id"
            multiple={true}
            onChange={handleChange}
            noSelection=""
            options={rulelineObjects}
            selected={ruleline?.valueType && ruleline.value ? getSelectedIndices() : []}
            searchOptions={{ enabled: true, placeholder: t("search") }}
            styles={{ minWidth: 300 }}
            sx={{
              body: {
                // maxHeight:300,
              }
            }}
          >
            {({anchorEl,onClick}) => (
              <Box
                onClick={onClick}
                sx={{
                  width: 1,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text noBreak>
                  {valueArray.length > 1
                    ? valueArray.length + " " + t("selected")
                    : firstSelectedOption?.name}
                </Text>
              </Box>
            )}
          </DropdownBaseV2>
        </Box>
      )}
    </SHQuickAction>
  );
};
